import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, forkJoin, Observable, Subscription, take } from 'rxjs';
import { AddImageRequest, ApiImageResponse, ApiMessageResponse, ImageLibraryService, ImageTagResponse } from 'src/app/services/aws/image-library/image-library';
import { WidgetsModule } from 'src/app/widgets/widgets.module';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/widgets/confirm-dialog/confirm-dialog.component';
import imageCompression from 'browser-image-compression';



interface ImageAsset {
    id: number;
    key: string;
    name: string;
    tagId: number;
    tagLabel: string;
    fileName: string;
    keywords: string[];
    uploadedBy: string;
    uploadedAt: Date;
    sizeInKb: number;
    isDefault: boolean;
    contentUrl?: string;
}

type SortKey = 'uploadDesc' | 'uploadAsc' | 'nameAsc' | 'nameDesc' | 'tagAsc' | 'tagDesc';
type TagFilterValue = 'all' | number;
type StatusMessage = { type: 'success' | 'error' | 'info'; message: string };

const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg'];

@Component({
    selector: 'app-image-library',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, WidgetsModule],
    templateUrl: './image-library.component.html',
    styleUrls: ['./image-library.component.scss'],

})
export class ImageLibraryComponent implements OnDestroy, OnInit {
    readonly imageForm: FormGroup;
    public showGlobalSpinner: boolean = false;
    imageNameExists: boolean = false;
    horizontalPosition: MatSnackBarHorizontalPosition = 'right';
    verticalPosition: MatSnackBarVerticalPosition = 'top';
    breadcrumbOptions = {
        breadcrumb: [
            {
                label: 'Dashboard',
                itemclass: 'Breadcrumb-item',
                linkclass: 'Breadcrumb-link',
                link: '/admin/dashboard'
            },
            {
                label: 'Image Library',
                itemclass: 'Breadcrumb-item',
                linkclass: 'Breadcrumb-link',
                link: '/admin/settings/imageLibrary'
            }
        ],
    };
    guidelinesExpanded = false;
    errorMessage: any;
    tagsCatalog: ImageTagResponse[] = [];
    private imageNameSub?: Subscription;
    private selectedFile: File | null = null;
    private fileInputRef: HTMLInputElement | null = null;
    readonly REQUIRED_WIDTH = 1125;
    readonly REQUIRED_HEIGHT = 600;
    // NEW: file indication summary used by HTML
    selectedFileSummary: null | {
        name: string;
        sizeKb: number;
        width: number;
        height: number;
        isValid: boolean;
        message?: string;
    } = null;

    readonly constraints = {
        maxSizeBytes: 10 * 1024 * 1024,
        acceptedTypes: ACCEPTED_MIME_TYPES.join(', '),
        requiredWidth: 1125,
        requiredHeight: 600
    };

    constructor(
        private readonly fb: FormBuilder,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private readonly imageLibraryService: ImageLibraryService
    ) {
        this.imageForm = this.fb.group({
            imageName: ['', [Validators.required, Validators.maxLength(50)]],
            tagId: [null, Validators.required],
            keywords: [''],
            isDefault: [false],
            file: [null as File | null, Validators.required]
        });

    }



    libraryStatus: StatusMessage | null = null;
    modalStatus: StatusMessage | null = null;
    syncState: 'idle' | 'pending' | 'synced' = 'idle';
    isModalOpen = false;
    isPreviewOpen = false;
    previewedAsset: ImageAsset | null = null;
    isLoadingLibrary = false;
    loadError: string | null = null;

    filters = {
        search: '',
        tagId: 'all' as TagFilterValue,
        sortBy: 'uploadDesc' as SortKey,
        showDefaultsOnly: false
    };

    private statusTimeout: ReturnType<typeof setTimeout> | null = null;
    private syncTimeout: ReturnType<typeof setTimeout> | null = null;
    library: ImageAsset[] = []

    get filteredImages(): ImageAsset[] {
        const { search, tagId, sortBy, showDefaultsOnly } = this.filters;
        let result = [...this.library];

        if (search.trim()) {
            const lower = search.toLowerCase();
            result = result.filter(
                (asset) =>
                    asset.name.toLowerCase().includes(lower)
                // TODO: Add search filters for tagLabel, fileName, and keywords if needed in future.
            );
        }

        if (tagId !== 'all') {
            result = result.filter((asset) => asset.tagId === tagId);
        }

        if (showDefaultsOnly) {
            result = result.filter((asset) => asset.isDefault);
        }

        result.sort((a, b) => {
            switch (sortBy) {
                case 'uploadAsc':
                    return a.uploadedAt.getTime() - b.uploadedAt.getTime();
                case 'nameAsc':
                    return a.name.localeCompare(b.name);
                case 'nameDesc':
                    return b.name.localeCompare(a.name);
                case 'tagAsc':
                    return a.tagLabel.localeCompare(b.tagLabel);
                case 'tagDesc':
                    return b.tagLabel.localeCompare(a.tagLabel);
                default:
                    return b.uploadedAt.getTime() - a.uploadedAt.getTime();
            }
        });

        return result;
    }

    get defaultCount(): number {
        return this.tagsCatalog.filter((tag) => this.hasDefaultForTag(tag.id)).length;
    }

    get missingDefaultTags(): string[] {
        return this.tagsCatalog.filter((tag) => !this.hasDefaultForTag(tag.id)).map((tag) => tag.tagKey);
    }

    readonly trackByImage = (_: number, asset: ImageAsset) => asset.id;

    ngOnInit(): void {
        this.loadRemoteImages();
        this.imageNameSub = this.imageForm.get('imageName')?.valueChanges
            .subscribe(name => {
                if (!name) {
                    this.imageNameExists = false;
                    return;
                }
                const lower = name.trim().toLowerCase();
                this.imageNameExists = this.library.some(img =>
                    img.name.trim().toLowerCase() === lower
                );
                const ctrl = this.imageForm.get('imageName');
                if (!ctrl) {
                    return;
                }
                if (this.imageNameExists) {
                    ctrl.setErrors({ nameExists: true });
                } else if (ctrl.hasError('nameExists')) {
                    ctrl.setErrors(null);
                    ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                }
            });
    }

    ngOnDestroy(): void {
        if (this.statusTimeout) {
            clearTimeout(this.statusTimeout);
        }
        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
        }
        this.imageNameSub?.unsubscribe();
    }

    private hasDefaultForTag(tagId: number): boolean {
        return this.library.some((asset) => asset.tagId === tagId && asset.isDefault);
    }

    updateSearch(search: string): void {
        this.filters = { ...this.filters, search };
    }

    updateTagFilter(value: string): void {
        const tagId: TagFilterValue = value === 'all' ? 'all' : Number(value);
        this.filters = { ...this.filters, tagId };
    }

    updateSort(sortBy: SortKey): void {
        this.filters = { ...this.filters, sortBy };
    }

    openModal(): void {
        this.imageForm.reset({
            imageName: '',
            tagId: this.tagsCatalog[0]?.id ?? null,
            keywords: '',
            isDefault: false,
            file: null
        });
        this.imageForm.markAsPristine();
        this.imageForm.markAsUntouched();
        this.modalStatus = null;
        this.isModalOpen = true;
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.modalStatus = null;
    }

    openPreview(asset: ImageAsset): void {
        if (!asset.contentUrl) {
            this.pushLibraryStatus(
                { type: 'info', message: 'This asset does not yet have a preview URL. Upload a new image to replace it.' },
                true
            );
            return;
        }
        this.previewedAsset = asset;
        this.isPreviewOpen = true;
    }

    closePreview(): void {
        this.isPreviewOpen = false;
        this.previewedAsset = null;
    }

    toggleDefaultsOnly(value: boolean): void {
        this.filters = { ...this.filters, showDefaultsOnly: value };
    }

    async onFileSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        this.fileInputRef = input;
        const fileControl = this.imageForm.get('file');
        const file = input.files?.[0];
        // reset summary each time
        this.selectedFileSummary = null;
        if (!file || !fileControl) {
            this.selectedFile = null;
            fileControl?.setValue(null);
            fileControl?.markAsDirty();
            fileControl?.markAsTouched();
            return;
        }
        // type check
        if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
            this.selectedFile = null;
            fileControl.setValue(null);
            fileControl.setErrors({ invalidType: true });
            fileControl.markAsDirty();
            fileControl.markAsTouched();
            this.selectedFileSummary = {
                name: file.name,
                sizeKb: Math.round(file.size / 1024),
                width: 0,
                height: 0,
                isValid: false,
                message: `Only PNG or JPG images are allowed.`
            };
            input.value = '';
            return;
        }
        // ORIGINAL FILE SIZE CHECK (keep)
        const originalFileSizeMB = file.size / (1024 * 1024);
        if (originalFileSizeMB > 10) {
            this.selectedFile = null;
            fileControl.setValue(null);
            fileControl.setErrors({ originalMaxSizeExceeded: true });
            fileControl.markAsDirty();
            fileControl.markAsTouched();
            this.showGlobalSpinner = false;
            this.selectedFileSummary = {
                name: file.name,
                sizeKb: Math.round(file.size / 1024),
                width: 0,
                height: 0,
                isValid: false,
                message: `File too large. Max allowed is 10MB.`
            };
            this.snackBar.open(
                `File too large (${originalFileSizeMB.toFixed(2)}MB). Max allowed is 10MB`,
                'OK',
                {
                    duration: 8000,
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    panelClass: ['error-snackbar']
                }
            );
            input.value = '';
            return;
        }
        // Dimension check BEFORE compression
        let originalDims;
        try {
            originalDims = await this.getImageDimensions(file);
        } catch (error) {
            this.selectedFile = null;
            fileControl.setValue(null);
            fileControl.setErrors({ dimensionReadError: true });
            fileControl.markAsDirty();
            fileControl.markAsTouched();
            this.selectedFileSummary = {
                name: file.name,
                sizeKb: Math.round(file.size / 1024),
                width: 0,
                height: 0,
                isValid: false,
                message: 'Could not read image dimensions. Please try a different file.'
            };
            input.value = '';
            return;
        }
        const originalDimsValid =
            originalDims.width === this.REQUIRED_WIDTH &&
            originalDims.height === this.REQUIRED_HEIGHT;
        this.selectedFileSummary = {
            name: file.name,
            sizeKb: Math.round(file.size / 1024),
            width: originalDims.width,
            height: originalDims.height,
            isValid: originalDimsValid,
            message: originalDimsValid
                ? undefined
                : `Image must be exactly ${this.REQUIRED_WIDTH}×${this.REQUIRED_HEIGHT}px (15:8).`
        };
        if (!originalDimsValid) {
            this.selectedFile = null;
            fileControl.setValue(null);
            fileControl.setErrors({ invalidDimensions: true });
            fileControl.markAsDirty();
            fileControl.markAsTouched();
            input.value = '';
            return;
        }
        try {
            // Show spinner using YOUR existing variable (minimal change)
            this.showGlobalSpinner = true;
            //Compress file (allowed because original size was valid)
            const compressedFile = await imageCompression(file, {
                maxSizeMB: 1.5,
                useWebWorker: true
            });
            this.showGlobalSpinner = false;
            // Dimension check AFTER compression
            let compressedDims;
            try {
                compressedDims = await this.getImageDimensions(compressedFile);
            } catch (e) {
                // If we cannot read dimensions from the compressed image, treat it as a dimension read error
                this.showGlobalSpinner = false;
                this.selectedFile = null;
                fileControl.setValue(null);
                fileControl.setErrors({ dimensionReadError: true });
                fileControl.markAsDirty();
                fileControl.markAsTouched();
                input.value = '';
                return;
            }
            const compressedDimsValid =
                compressedDims.width === this.REQUIRED_WIDTH &&
                compressedDims.height === this.REQUIRED_HEIGHT;
            // Update UI summary to reflect final file
            this.selectedFileSummary = {
                name: compressedFile.name,
                sizeKb: Math.round(compressedFile.size / 1024),
                width: compressedDims.width,
                height: compressedDims.height,
                isValid: compressedDimsValid,
                message: compressedDimsValid
                    ? undefined
                    : `After compression, image dimensions changed. Must remain ${this.REQUIRED_WIDTH}×${this.REQUIRED_HEIGHT}px.`
            };
            if (!compressedDimsValid) {
                this.selectedFile = null;
                fileControl.setValue(null);
                fileControl.setErrors({ invalidDimensions: true });
                fileControl.markAsDirty();
                fileControl.markAsTouched();
                input.value = '';
                return;
            }
            // valid
            this.selectedFile = compressedFile;
            fileControl.setValue(true);
            fileControl.setErrors(null);
            fileControl.markAsDirty();
            fileControl.markAsTouched();
        } catch (err) {
            this.showGlobalSpinner = false;
            this.selectedFile = null;
            fileControl.setValue(null);
            fileControl.setErrors({ invalid: true });
            fileControl.markAsDirty();
            fileControl.markAsTouched();
            this.selectedFileSummary = {
                name: file.name,
                sizeKb: Math.round(file.size / 1024),
                width: 0,
                height: 0,
                isValid: false,
                message: 'Something went wrong while processing the image.'
            };
        }
    }

    async submitImage(): Promise<void> {
        this.imageForm.markAllAsTouched();
        if (this.imageForm.invalid || !this.selectedFile) {
            return;
        }

        const { imageName, tagId, isDefault } = this.imageForm.value;
        const fileRef = this.selectedFile;

        const normalizedName = imageName?.trim() ?? '';
        const normalizedTagId = Number(tagId);
        this.syncState = 'pending';
        this.modalStatus = { type: 'info', message: 'Uploading image to Lakehouse...' };

        try {
            this.showGlobalSpinner = true;
            //Convert compressed file to Base64
            const base64 = await this.fileToBase64(fileRef);
            //Keep payload EXACTLY the same format backend already accepts
            const payload: AddImageRequest = {
                key: normalizedName,
                fileName: fileRef.name,
                content: base64,
                tagId: normalizedTagId,
                isDefault: Boolean(isDefault)
            };
            const response = await firstValueFrom(this.imageLibraryService.uploadImage(payload));
            if (!this.isApiSuccess(response as ApiMessageResponse)) {
                this.showGlobalSpinner = false;
                const message = (response as ApiMessageResponse).data?.[0] ?? 'Failed to upload the image.';
                this.modalStatus = { type: 'error', message };
                this.syncState = 'idle';
                return;
            } this.loadRemoteImages();
            this.imageForm.reset({
                imageName: '',
                tagId: this.tagsCatalog[0]?.id ?? null,
                isDefault: false,
            });
            this.syncState = 'synced';
            this.modalStatus = null;
            this.isModalOpen = false;
            this.showGlobalSpinner = false;
            this.snackBar.open('Image added', 'OK', {
                duration: 5000,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: ['success-snackbar']
            });

            // prevent InvalidStateError
            if (this.fileInputRef) {
                this.fileInputRef.value = '';
            }

            // Clear the Form Control state and the stored file property
            this.imageForm.get('file')?.setValue(null);
            this.selectedFile = null;
        } catch (error) {
            this.showGlobalSpinner = false;
            this.syncState = 'idle';
            this.snackBar.open('Something went wrong, please try again later.', 'OK', {
                duration: 8000,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: ['error-snackbar']
            });
        }
    }



    async deleteImage(asset: ImageAsset): Promise<void> {
        if (asset.isDefault) {
            this.snackBar.open('Default images are protected. Reassign the default before deleting.', 'OK', {
                duration: 8000,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: ['error-snackbar']
            });
            return;
        }

        this.openConfirmDialog(`Are you sure you want to delete ${asset.name}? This cannot be undone.`)
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    this.showGlobalSpinner = true;

                    this.imageLibraryService.deleteImage(asset.id)
                        .pipe(take(1))
                        .subscribe(() => {
                            this.showGlobalSpinner = false;
                            this.loadRemoteImages();
                            this.snackBar.open(`${asset.name} removed from the library.`, 'OK', {
                                duration: 5000,
                                horizontalPosition: this.horizontalPosition,
                                verticalPosition: this.verticalPosition,
                                panelClass: ['success-snackbar']
                            });
                        },
                            (err) => {
                                this.showGlobalSpinner = false;
                                const message = err?.response?.data === "ImageInUse"
                                    ? `This image can't be deleted because it's linked to a House Event`
                                    : `Something went wrong, please try again later.`;
                                this.snackBar.open(message, 'OK', {
                                    duration: 8000,
                                    horizontalPosition: this.horizontalPosition,
                                    verticalPosition: this.verticalPosition,
                                    panelClass: ['error-snackbar']
                                });
                            });

                }
            });
    }

    private openConfirmDialog(body: string): Observable<boolean> {
        return this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: null,
                body: body,
                buttonTextNo: "Cancel",
                buttonTextYes: "Yes, delete it!"
            },
            panelClass: 'notification-submit-dialog'
        }).afterClosed();
    }
    async setAsDefault(asset: ImageAsset): Promise<void> {
        if (asset.isDefault) {
            return;
        }
        try {
            const response = await firstValueFrom(this.imageLibraryService.updateDefault(asset.id));
            if (!this.isApiSuccess(response as ApiMessageResponse)) {
                const message = (response as ApiMessageResponse).data?.[0] ?? 'Could not update the default image.';
                this.pushLibraryStatus({ type: 'error', message }, false);
                return;
            }
            this.loadRemoteImages();
            this.snackBar.open(`${asset.name} is now the default for the ${asset.tagLabel} tag.`, 'OK', {
                duration: 5000,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: ['success-snackbar']
            });


        } catch (error) {
            this.snackBar.open('Something went wrong, please try again later.', 'OK', {
                duration: 8000,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: ['error-snackbar']
            });

        }
    }

    private loadRemoteImages(): void {
        this.isLoadingLibrary = true;
        this.showGlobalSpinner = true;
        this.loadError = null;
        forkJoin([
            this.imageLibraryService.getHouseEventTags(),
            this.imageLibraryService.fetchImages()
        ])
            .subscribe({
                next: ([tags, images]) => {
                    this.tagsCatalog = tags;
                    if (this.tagsCatalog.length > 0) {
                        this.imageForm.patchValue({
                            tagId: this.tagsCatalog[0]?.id
                        });
                    }
                    const mapped = images.map(img => this.mapApiImage(img));
                    this.library = mapped.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
                    this.isLoadingLibrary = false;
                    this.showGlobalSpinner = false;
                },
                error: () => {
                    this.isLoadingLibrary = false;
                    this.showGlobalSpinner = false;
                    this.snackBar.open("Something went wrong, please try again later.", 'OK', {
                        duration: 8000,
                        horizontalPosition: this.horizontalPosition,
                        verticalPosition: this.verticalPosition,
                        panelClass: ['error-snackbar']
                    });
                }
            });
    }




    private mapApiImage(image: ApiImageResponse): ImageAsset {
        const tagId = image.tagId ?? 0;
        const imageId = image.id ?? 0;
        return {
            id: image.id,
            key: image.key ?? image.fileName ?? `image-${Date.now()}`,
            name: image.key ?? image.fileName ?? 'Unnamed Image',
            tagId,
            tagLabel: this.getTagLabel(tagId),
            fileName: image.fileName ?? 'unknown.png',
            keywords: [],
            uploadedBy: 'Lakehouse API',
            uploadedAt: image.uploadDate ? new Date(image.uploadDate) : new Date(),
            sizeInKb: image.sizeInKb ?? 0,
            isDefault: this.getDefaultImage(imageId),
            contentUrl: image.content
        };
    }

    private getTagLabel(tagId: number): string {
        const tag = this.tagsCatalog.find(t => t.id === tagId);
        if (!tag) {
            return 'Unknown Tag';
        }
        return tag.tagKey;
    }



    private getDefaultImage(imageId: number): boolean {
        return this.tagsCatalog.some(t => t.imageId === imageId);
    }

    private validateFile(file: File): string | null {
        if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
            return `Unsupported file type. Accepted formats: ${ACCEPTED_MIME_TYPES.join(', ')}`;
        }
        if (file.size > this.constraints.maxSizeBytes) {
            return 'File exceeds 10 MB limit. Please compress or choose a smaller file.';
        }
        return null;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result;
                if (typeof result === 'string') {
                    resolve(result.split(',')[1] ?? result);
                } else {
                    reject(new Error('Unable to read file content.'));
                }
            };
            reader.onerror = () => reject(reader.error ?? new Error('Unable to read file content.'));
            reader.readAsDataURL(file);
        });
    }

    private isApiSuccess(response: ApiMessageResponse): boolean {
        return response.isComplete;
    }

    private queueSyncReset(): void {
        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
        }
        this.syncTimeout = setTimeout(() => {
            this.syncState = 'idle';
            this.syncTimeout = null;
        }, 5000);
    }

    private pushLibraryStatus(status: StatusMessage, autoClear = true): void {
        this.libraryStatus = status;
        if (this.statusTimeout) {
            clearTimeout(this.statusTimeout);
        }
        if (autoClear) {
            this.statusTimeout = setTimeout(() => {
                this.libraryStatus = null;
                this.statusTimeout = null;
            }, 5000);
        }
    }

    // helper to read image dimensions (needed for 1125x600 validation)
    private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
                const width = img.naturalWidth;
                const height = img.naturalHeight;
                URL.revokeObjectURL(url);
                resolve({ width, height });
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Unable to read image dimensions'));
            };
            img.src = url;
        });
    }
}